import type { DashboardAssistantConversationSummary } from '../types';

export function getSessionTitle(input: string) {
  const compact = input.replace(/\s+/g, ' ').trim();
  if (!compact) {
    return 'New chat';
  }
  return compact.length > 48 ? `${compact.slice(0, 48)}...` : compact;
}

export function getConversationTimestamp(conversation: DashboardAssistantConversationSummary) {
  return conversation.lastMessageAt || conversation.startedAt;
}

export function getRelativeTimeLabel(value: string | null) {
  if (!value) {
    return '';
  }

  const timestamp = new Date(value).getTime();
  if (Number.isNaN(timestamp)) {
    return '';
  }

  const diffMs = Math.max(Date.now() - timestamp, 0);
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;
  const weekMs = 7 * dayMs;

  if (diffMs < hourMs) {
    return `${Math.max(Math.floor(diffMs / minuteMs), 1)}m`;
  }
  if (diffMs < dayMs) {
    return `${Math.floor(diffMs / hourMs)}h`;
  }
  if (diffMs < weekMs) {
    return `${Math.floor(diffMs / dayMs)}d`;
  }
  return `${Math.floor(diffMs / weekMs)}w`;
}

export function getHistoryGroupLabel(value: string | null) {
  if (!value) {
    return 'Older';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Older';
  }

  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.floor(
    (startOfToday.getTime() - startOfDate.getTime()) / (24 * 60 * 60 * 1000),
  );

  if (diffDays <= 0) {
    return 'Today';
  }
  if (diffDays === 1) {
    return 'Yesterday';
  }
  if (diffDays < 7) {
    return 'This week';
  }
  if (diffDays < 28) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }
  return 'Older';
}

export function groupConversations(conversations: DashboardAssistantConversationSummary[]) {
  return conversations.reduce<
    Array<{ label: string; conversations: DashboardAssistantConversationSummary[] }>
  >((groups, conversation) => {
    const label = getHistoryGroupLabel(getConversationTimestamp(conversation));
    const existingGroup = groups.find((group) => group.label === label);

    if (existingGroup) {
      existingGroup.conversations.push(conversation);
    } else {
      groups.push({ label, conversations: [conversation] });
    }

    return groups;
  }, []);
}
